import { useState } from 'react'
import { Bar, BarChart, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ReportPager, ReportSection } from '@/components/reports/ReportSection'
import {
  ReportStates,
  downloadCsv,
  tooltipStyle,
  typeLabel,
} from '@/components/reports/procedures/reportShared'
import { useInventoryConsumptionReport } from '@/hooks/useProcedureVolumeReport'
import { reportFileName, toCsv } from '@/lib/reportCsv'
import type { ReportFilterSelection } from '@/lib/reportFilters'
import { windowLabel, type ReportTimeFrame } from '@/lib/reportRange'

/**
 * Report 2.2 — inventory consumption by procedure.
 *
 * Two questions, one set of rows: what a procedure consumes, and which
 * procedures consume a given item. They are the same data read along two axes,
 * which is why they are one report rather than two.
 *
 * ## CONSUMED only
 *
 * A reservation is a hold that may still be released. Counting it would report
 * stock as used that is sitting on the shelf, so the figures here are strictly
 * what left the building.
 *
 * ## Quantity is not a single unit
 *
 * Materials are counted in vials, boxes and millilitres, so the total is a sum
 * of different things and is labelled "units" rather than pretending otherwise.
 * The per-material table is where a number means something exact, which is why
 * it carries the detail rather than the headline.
 */
const BAR = '#2f6f9f'

const PAGE_SIZE = 20

export function InventoryConsumptionReport({
  frame,
  filters,
}: {
  frame: ReportTimeFrame
  filters: ReportFilterSelection
}) {
  const query = useInventoryConsumptionReport(frame, filters)
  const [page, setPage] = useState(1)
  const state = ReportStates(query)
  if (state) return state
  const report = query.data
  if (!report) return null

  const byProcedure = report.byProcedure.map((p) => ({
    name: typeLabel(p.type),
    qty: p.qty,
    label: String(p.qty),
    runs: p.runs,
    materials: p.materials,
  }))

  const pageCount = Math.max(1, Math.ceil(report.byMaterial.length / PAGE_SIZE))
  const current = Math.min(page, pageCount)
  const start = (current - 1) * PAGE_SIZE
  const rows = report.byMaterial.slice(start, start + PAGE_SIZE)

  const exportCsv = () => {
    downloadCsv(
      toCsv(
        ['Material', 'Category', 'Quantity', 'Consumed by', 'Runs'],
        report.byMaterial.map((m) => [
          m.materialName,
          m.category,
          m.qty,
          m.types.map(typeLabel).join(' / '),
          m.runs,
        ]),
      ),
      reportFileName('inventory-consumption', report.window),
    )
  }

  return (
    <div className='flex flex-col gap-4 p-4'>
      <ReportSection
        title='Consumption'
        subtitle={windowLabel({ from: report.window.from, to: report.window.to })}
      >
        <div className='flex flex-wrap items-end gap-x-8 gap-y-3 p-4'>
          <div>
            <p className='text-5xl font-semibold tabular-nums' style={{ color: BAR }}>
              {report.totalQty}
            </p>
            {/* Vials, boxes and millilitres summed together — said plainly
                rather than dressed up as one unit. */}
            <p className='mt-1 text-sm text-muted-foreground'>units consumed, across all materials</p>
          </div>
          <dl className='flex flex-wrap gap-x-6 gap-y-2'>
            <Stat label='Materials' value={report.byMaterial.length} />
            <Stat label='Procedure types' value={report.byProcedure.length} />
          </dl>
        </div>
        {report.unattributedQty > 0 && (
          <p className='px-4 pb-4 text-xs text-muted-foreground'>
            {report.unattributedQty} consumed by steps outside any run.
          </p>
        )}
      </ReportSection>

      {byProcedure.length > 0 && (
        <ReportSection title='By procedure type'>
          <div className='p-4'>
            <ResponsiveContainer width='100%' height={Math.max(140, byProcedure.length * 46)}>
              <BarChart data={byProcedure} layout='vertical' margin={{ left: 8, right: 48, top: 4, bottom: 4 }}>
                <XAxis type='number' hide />
                <YAxis
                  type='category'
                  dataKey='name'
                  width={140}
                  tick={{ fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={tooltipStyle}
                  formatter={(_v, _n, item) => {
                    const p = item.payload as (typeof byProcedure)[number]
                    return [`${p.qty} units · ${p.materials} materials · ${p.runs} runs`, 'Consumed']
                  }}
                />
                <Bar dataKey='qty' fill={BAR} radius={[0, 4, 4, 0]} barSize={22}>
                  <LabelList dataKey='label' position='right' style={{ fontSize: 11 }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </ReportSection>
      )}

      <ReportSection
        title='By material'
        onExport={report.byMaterial.length > 0 ? exportCsv : undefined}
      >
        {report.byMaterial.length === 0 ? (
          <p className='px-4 py-6 text-xs text-muted-foreground'>Nothing consumed in this window.</p>
        ) : (
          <>
            <div className='overflow-x-persistent'>
              <table className='w-full text-xs'>
                <thead className='border-b bg-muted/40 text-left text-muted-foreground'>
                  <tr>
                    <th className='px-3 py-2 font-medium'>Material</th>
                    <th className='px-3 py-2 font-medium'>Category</th>
                    <th className='px-3 py-2 text-right font-medium'>Quantity</th>
                    {/* The "which procedures require this item" half. */}
                    <th className='px-3 py-2 font-medium'>Consumed by</th>
                    <th className='px-3 py-2 text-right font-medium'>Runs</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((m) => (
                    <tr key={m.materialId} className='border-b last:border-0'>
                      <td className='px-3 py-1.5'>{m.materialName}</td>
                      <td className='px-3 py-1.5'>{m.category}</td>
                      <td className='px-3 py-1.5 text-right font-medium tabular-nums'>{m.qty}</td>
                      <td className='px-3 py-1.5'>{m.types.map(typeLabel).join(', ')}</td>
                      <td className='px-3 py-1.5 text-right tabular-nums'>{m.runs}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ReportPager
              page={current}
              pageCount={pageCount}
              from={start + 1}
              to={start + rows.length}
              total={report.byMaterial.length}
              onPage={setPage}
            />
          </>
        )}
      </ReportSection>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <dt className='text-xs text-muted-foreground'>{label}</dt>
      <dd className='text-xl font-semibold tabular-nums text-foreground'>{value}</dd>
    </div>
  )
}
